import { utilities } from 'utils/utilities';
import { AppointmentRuleType, normalizeAppointmentRuleType } from './AppointmentRuleType';
import type { AppointmentRuleDto } from './AppointmentRuleDto';

/**
 * Domain model for an appointment rule on a workflow template.
 * `configurationJson` is untrusted server text: it is parsed once, defensively,
 * in `fromDto`; render `minimumNoticeHours`, never the raw JSON.
 */
export class AppointmentRule {
  id: string;
  workflowTemplateId: string;
  ruleType: AppointmentRuleType;
  configurationJson: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string | null;
  private readonly parsedMinimumNoticeHours: number | null;

  constructor(
    id: string = utilities.DEFAULT_GUID,
    workflowTemplateId: string = utilities.DEFAULT_GUID,
    ruleType: AppointmentRuleType = AppointmentRuleType.TimeBasedRestriction,
    configurationJson: string = '',
    isActive: boolean = true,
    createdAt: string = '',
    updatedAt: string | null = null,
    minimumNoticeHours: number | null = null,
  ) {
    this.id = id;
    this.workflowTemplateId = workflowTemplateId;
    this.ruleType = ruleType;
    this.configurationJson = configurationJson;
    this.isActive = isActive;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.parsedMinimumNoticeHours = minimumNoticeHours;
  }

  /** Parsed + shape-validated config; null when absent or unparsable. */
  get minimumNoticeHours(): number | null {
    return this.parsedMinimumNoticeHours;
  }

  toDto(): AppointmentRuleDto {
    return {
      id: this.id,
      workflowTemplateId: this.workflowTemplateId,
      ruleType: this.ruleType,
      configurationJson: this.configurationJson,
      isActive: this.isActive,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  static fromDto(dto?: Partial<AppointmentRuleDto> | AppointmentRule | null): AppointmentRule {
    if (!dto) return new AppointmentRule();
    if (dto instanceof AppointmentRule) return dto;

    const configurationJson = dto.configurationJson ?? '';
    return new AppointmentRule(
      dto.id ?? utilities.DEFAULT_GUID,
      dto.workflowTemplateId ?? utilities.DEFAULT_GUID,
      normalizeAppointmentRuleType(dto.ruleType),
      configurationJson,
      dto.isActive ?? true,
      dto.createdAt ?? '',
      dto.updatedAt ?? null,
      parseMinimumNoticeHours(configurationJson),
    );
  }
}

/**
 * Casing-tolerant (`MinimumNoticeHours ?? minimumNoticeHours`) parse of the
 * rule configuration. Never throws: malformed JSON, non-object payloads, and
 * values that are not positive numbers all yield null.
 */
function parseMinimumNoticeHours(configurationJson: string): number | null {
  if (!configurationJson) return null;
  try {
    const parsed: unknown = JSON.parse(configurationJson);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const config = parsed as Record<string, unknown>;
    const hours = config['MinimumNoticeHours'] ?? config['minimumNoticeHours'];
    return typeof hours === 'number' && hours > 0 ? hours : null;
  } catch {
    return null;
  }
}
